// pages/ReuploadDocumentsPage.js
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const FileInput = ({ id, label, accept, onChange, value, error }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}
    </label>
    <input 
      type="file" 
      id={id} 
      name={id} 
      onChange={onChange} 
      accept={accept}
      className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 dark:file:bg-orange-900/50 file:text-orange-700 dark:file:text-orange-200 hover:file:bg-orange-100 dark:hover:file:bg-orange-900" 
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
      Maximum file size: 5MB. Allowed types: {accept}
    </p>
  </div>
);

export default function ReuploadDocumentsPage() {
  const { user, token } = useAuth(); // Get both user and token
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    profilePhoto: null,
    idDocument: null
  });

  const [fileErrors, setFileErrors] = useState({
    profilePhoto: '',
    idDocument: ''
  });

  const updateFormData = (field, file) => {
    // Validate file size (5MB max)
    if (file && file.size > 5 * 1024 * 1024) {
      setFileErrors(prev => ({
        ...prev,
        [field]: 'File size must be less than 5MB'
      }));
      return;
    }

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const validDocumentTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    
    if (field === 'profilePhoto' && !validImageTypes.includes(file.type)) {
      setFileErrors(prev => ({
        ...prev,
        [field]: 'Only JPEG and PNG images are allowed'
      }));
      return;
    }

    if (field === 'idDocument' && !validDocumentTypes.includes(file.type)) {
      setFileErrors(prev => ({
        ...prev,
        [field]: 'Only JPEG, PNG, and PDF files are allowed'
      }));
      return;
    }

    setFileErrors(prev => ({ ...prev, [field]: '' }));
    setFormData(prev => ({ ...prev, [field]: file }));
  };

  const uploadFileToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetch(`${process.env.REACT_APP_API_URL}/user/upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}` // Use the separate token
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to upload ${type}`);
    }

    return await response.json();
  };

 // pages/ReuploadDocumentsPage.js - Updated handleSubmit function
const handleSubmit = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');

  if (!formData.profilePhoto && !formData.idDocument) {
    setError('Please select at least one file to upload');
    return;
  }

  setIsSubmitting(true);

  try {
    // Create FormData with correct field names
    const formDataToSend = new FormData();
    
    if (formData.profilePhoto) {
      formDataToSend.append('profilePhoto', formData.profilePhoto);
    }
    
    if (formData.idDocument) {
      formDataToSend.append('idDocument', formData.idDocument);
    }

    // Send both files in one request
    const response = await fetch(`${process.env.REACT_APP_API_URL}/user/upload-document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formDataToSend
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to upload documents');
    }

    const result = await response.json();
    
    setSuccess('Documents uploaded successfully!');
    setTimeout(() => {
      navigate('/recipient/dashboard');
    }, 2000);
    
  } catch (err) {
    console.error('Upload failed:', err);
    setError(err.message || 'Failed to upload documents. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  const hasFiles = formData.profilePhoto || formData.idDocument;

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Re-upload Lost Documents
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Please re-upload your profile photo and ID documents that were lost due to a technical issue.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-green-700">{success}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <FileInput
            id="profilePhoto"
            label="Profile Photo"
            accept="image/jpeg, image/png"
            onChange={(e) => updateFormData('profilePhoto', e.target.files[0])}
            error={fileErrors.profilePhoto}
          />

          <FileInput
            id="idDocument"
            label="ID Document"
            accept="image/jpeg, image/png, application/pdf"
            onChange={(e) => updateFormData('idDocument', e.target.files[0])}
            error={fileErrors.idDocument}
          />

          <div className="flex justify-between pt-4">
            <button
              type="button"
              onClick={() => navigate('/recipient/dashboard')}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Skip for Now
            </button>
            
            <button
              type="submit"
              disabled={!hasFiles || isSubmitting || Object.values(fileErrors).some(err => err)}
              className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Uploading...' : 'Upload Documents'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}